/**
 * Reach events (M3 plan §2.6.4): a second instance of M1's event ring (`createEventRing()` from
 * src/combat/events.ts) with M3's own type codes. The HUD, audio, VFX and toasts each keep their own cursor and
 * read it with `firstReadable` / `eventSlot`, exactly like the combat ring. Allocation-free (D-014).
 *
 * Field mapping of `emitReach(ring, type, a, b, x, y, z, amount)`: `eid` = a, `other` = b, `weapon` = 0 and the
 * direction fields 0. `x`, `y` and `z` are the float64 world position where one applies (0 otherwise).
 *
 * | Code | a | b | amount |
 * |---|---|---|---|
 * | systemEnter | system index | arrival (0 relay, 1 station, 2 location, 3 home, 4 respawn) | — |
 * | systemLeave | system index | — | — |
 * | transitStart | relay index | destination system index | — |
 * | transitEnd | destination system index | — | — |
 * | cruiseCharge | 0 start, 1 cancel | — | — |
 * | cruiseEngage | — | — | allowed speed (m/s) |
 * | cruiseDrop | — | 0 auto, 1 manual, 2 emergency, 3 interdicted | — |
 * | massLockDenied | — | reason (index in `MassLockReason` order) | — |
 * | laneEnter, laneExit, laneInterrupted | lane index | ring index | — |
 * | interdictStart | group id | — | — |
 * | interdictEnd | group id | 0 escaped, 1 failed | — |
 * | scanCharge, scanPing | — | — | radius (m; 0 = the whole system) |
 * | poiRevealed | system index | POI index | — |
 * | systemCharted, systemRumoured | system index | — | — |
 * | chartValue | system index | — | credits accrued |
 * | chartsSold | — | — | credits |
 * | relayScanned, rebootStart | relay index | — | — |
 * | partsDelivered | relay index | — | quantity |
 * | rebootWave | relay index | wave number | — |
 * | rebootEnd | relay index | 0 online, 1 destroyed, 2 aborted | — |
 * | sponsorChosen | relay index | faction index (factions.json order) | — |
 * | laneRingRepaired | lane-ring index | — | — |
 * | cacheOpened | POI index | — | credits |
 * | beaconRead, baseAlert, baseCleared | POI index | — | — |
 * | respawnTransfer | system index | — | — |
 * | saved, saveFailed | — | 0 auto, 1 manual | — |
 */
import { createEventRing, EVENT_MASK, type EventRing } from '../combat/events'

/** Reach event codes, numbered from 1 in the §2.6.4 order. Append only. */
export const REV = {
  systemEnter: 1,
  systemLeave: 2,
  transitStart: 3,
  transitEnd: 4,
  cruiseCharge: 5,
  cruiseEngage: 6,
  cruiseDrop: 7,
  massLockDenied: 8,
  laneEnter: 9,
  laneExit: 10,
  laneInterrupted: 11,
  interdictStart: 12,
  interdictEnd: 13,
  scanCharge: 14,
  scanPing: 15,
  poiRevealed: 16,
  systemCharted: 17,
  systemRumoured: 18,
  chartValue: 19,
  chartsSold: 20,
  relayScanned: 21,
  rebootStart: 22,
  partsDelivered: 23,
  rebootWave: 24,
  rebootEnd: 25,
  sponsorChosen: 26,
  laneRingRepaired: 27,
  cacheOpened: 28,
  beaconRead: 29,
  baseAlert: 30,
  baseCleared: 31,
  respawnTransfer: 32,
  saved: 33,
  saveFailed: 34,
} as const
export type ReachEventType = (typeof REV)[keyof typeof REV]
export type ReachEventName = keyof typeof REV

/** A fresh reach event ring (the same SoA ring as the combat events). */
export function createReachEvents(): EventRing {
  return createEventRing()
}

/** Appends a reach event, overwriting the oldest when full. Returns its sequence number. Allocation-free. */
export function emitReach(
  ring: EventRing,
  type: ReachEventType,
  a: number,
  b: number,
  x: number,
  y: number,
  z: number,
  amount: number,
): number {
  const seq = ring.head
  const s = seq & EVENT_MASK
  ring.type[s] = type
  ring.x[s] = x
  ring.y[s] = y
  ring.z[s] = z
  ring.dx[s] = 0
  ring.dy[s] = 0
  ring.dz[s] = 0
  ring.eid[s] = a
  ring.other[s] = b
  ring.weapon[s] = 0
  ring.amount[s] = amount
  ring.head = seq + 1
  return seq
}

const NAMES: readonly (ReachEventName | undefined)[] = (() => {
  const out: (ReachEventName | undefined)[] = []
  for (const name of Object.keys(REV) as ReachEventName[]) out[REV[name]] = name
  return out
})()

/** The name of a reach event code (debug API, logs); undefined for an unknown code. */
export function reachEventName(type: number): ReachEventName | undefined {
  return NAMES[type]
}
